import { t } from "ttag";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  DATA_TYPE_CODE_OPTIONS,
  ENG_UNIT_OPTIONS,
  engUnitLabel,
  REG_TYPE_NUMERIC_OPTIONS,
  RW_BOOL_OPTIONS,
  UI_TYPE_OPTIONS,
} from "../RegisterTable";
import { IModbusDeviceRow, IModbusDeviceUnit, MODBUS_DATA_TYPE_CODES } from "../types";

/**
 * Reads/writes the "Modbus Device" .xlsx format (CM-467/CM-674/CM-675).
 * Layout is fixed by the sample file attached to the story: device fields in
 * column B of rows 2-8, a blank row, then a register table from row 11
 * (header) / row 12 (first data row) onward.
 */

const SHEET_NAME = "Sheet1";
const HEADER_ROW = 11;
const FIRST_DATA_ROW = HEADER_ROW + 1;
const TEMPLATE_ROW_COUNT = 990; // matches the sample file's validated range (rows 12-1001)
const MAX_DATA_ROWS = 1000;

const REGISTER_COLUMNS = [
  "App Unit Name",
  "Parameter Name",
  "Type",
  "Eng Units",
  "Min",
  "Max",
  "Step",
  "R/W",
  "Scale",
  "Reg Type",
  "Register",
  "Bit #",
  "Data Type",
] as const;

const DEVICE_INFO_FIELDS = [
  "Device Name",
  "Manufacturer",
  "Model",
  "Slave ID",
  "Comm Timeout (ms)",
  "Host",
  "Port",
] as const;

export interface IDeviceInfoForExport {
  name: string;
  vendorName: string;
  modelName: string;
  slaveId: number | "";
  timeoutMs: number | "";
  host: string;
  port: number | "";
}

const labelFor = (options: readonly { value: string | number; label: string }[], value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const match = options.find((o) => o.value === value);
  return match ? match.label : String(value);
};

// Matches the sample file's styling exactly: brand-purple Roboto labels
// throughout, with the two section titles additionally highlighted with a
// light-blue fill so they read as section banners.
const LABEL_FONT: Partial<ExcelJS.Font> = { name: "Roboto", size: 11, color: { argb: "FF421A40" } };
const SECTION_FILL: ExcelJS.Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFA4C2F4" } };

const addDeviceInfoSection = (sheet: ExcelJS.Worksheet, device?: IDeviceInfoForExport) => {
  sheet.getCell("A1").value = "Device information";
  sheet.getCell("A1").font = LABEL_FONT;
  sheet.getCell("A1").fill = SECTION_FILL;

  const values: (string | number)[] = [
    device?.name ?? "",
    device?.vendorName ?? "",
    device?.modelName ?? "",
    device?.slaveId ?? "",
    device?.timeoutMs ?? "",
    device?.host ?? "",
    device?.port ?? "",
  ];
  DEVICE_INFO_FIELDS.forEach((label, i) => {
    const rowNumber = i + 2; // rows 2-8
    const labelCell = sheet.getCell(`A${rowNumber}`);
    labelCell.value = label;
    labelCell.font = LABEL_FONT;
    sheet.getCell(`B${rowNumber}`).value = values[i];
  });
};

const addRegisterSection = (sheet: ExcelJS.Worksheet, units: IModbusDeviceUnit[] = []): number => {
  const sectionCell = sheet.getCell(`A${HEADER_ROW - 1}`);
  sectionCell.value = "Functional units / Registers";
  sectionCell.font = LABEL_FONT;
  sectionCell.fill = SECTION_FILL;
  sheet.getRow(HEADER_ROW).values = [...REGISTER_COLUMNS];
  sheet.getRow(HEADER_ROW).font = LABEL_FONT;

  let rowNumber = FIRST_DATA_ROW;
  units.forEach((unit) => {
    unit.rows.forEach((row) => {
      sheet.getRow(rowNumber).values = [
        unit.name,
        row.name,
        labelFor(UI_TYPE_OPTIONS, row.uiType),
        engUnitLabel(row.units ?? undefined),
        row.minValue ?? "",
        row.maxValue ?? "",
        row.step ?? "",
        row.readOnly ? "R" : "R/W",
        row.multiplier ?? "",
        labelFor(REG_TYPE_NUMERIC_OPTIONS, row.registerType),
        row.registerAddress ?? "",
        row.bitNumber ?? "",
        labelFor(DATA_TYPE_CODE_OPTIONS, row.dataType),
      ];
      rowNumber += 1;
    });
  });

  return rowNumber - 1;
};

// exceljs's public .d.ts only types per-cell `dataValidation`, but the
// library also supports adding a validation to a whole range via
// `worksheet.dataValidations.add(address, validation)` (lib/doc/worksheet.js) —
// this type is missing from the shipped declarations, hence the cast.
type WorksheetWithRangeValidations = ExcelJS.Worksheet & {
  dataValidations: { add: (address: string, validation: Partial<ExcelJS.DataValidation>) => void };
};

const LISTS_SHEET_NAME = "Lists";

const addDropdownValidations = (workbook: ExcelJS.Workbook, sheet: ExcelJS.Worksheet, lastDataRow: number) => {
  const lastRow = Math.max(lastDataRow, FIRST_DATA_ROW + TEMPLATE_ROW_COUNT - 1);
  const range = (col: string) => `${col}${FIRST_DATA_ROW}:${col}${lastRow}`;
  const validations = (sheet as WorksheetWithRangeValidations).dataValidations;

  // Excel caps an inline List-validation formula (quoted, comma-separated
  // values) at 255 characters — the Data Type labels alone run past that,
  // which is invalid per the OOXML spec and makes Excel flag the file as
  // needing repair (more lenient readers like Google Sheets just accept it).
  // A range reference into a hidden helper sheet has no such limit.
  const listsSheet = workbook.addWorksheet(LISTS_SHEET_NAME, { state: "veryHidden" });
  const listRange = (col: string, values: string[]): string => {
    values.forEach((value, i) => {
      listsSheet.getCell(`${col}${i + 1}`).value = value;
    });
    return `${LISTS_SHEET_NAME}!$${col}$1:$${col}$${values.length}`;
  };

  validations.add(range("C"), { type: "list", formulae: [listRange("A", UI_TYPE_OPTIONS.map((o) => o.label))] });
  validations.add(range("D"), { type: "list", formulae: [listRange("B", ENG_UNIT_OPTIONS.map((o) => o.label))] });
  validations.add(range("H"), { type: "list", formulae: [listRange("C", RW_BOOL_OPTIONS.map((o) => o.label))] });
  validations.add(range("J"), {
    type: "list",
    formulae: [listRange("D", REG_TYPE_NUMERIC_OPTIONS.map((o) => o.label))],
  });
  validations.add(range("M"), {
    type: "list",
    formulae: [listRange("E", DATA_TYPE_CODE_OPTIONS.map((o) => o.label))],
  });
};

// Column widths sized to fit header and typical data content.
// A/B taken from the original sample file; C-M taken from the SATEC reference
// file which uses explicit widths wide enough for the dropdown option labels.
const COLUMN_WIDTHS: Record<string, number> = {
  A: 34,   // App Unit Name / "Functional units / Registers" section banner
  B: 30,   // Device info value column / Parameter Name
  C: 16,   // Type
  D: 12,   // Eng Units
  E: 10,   // Min
  F: 10,   // Max
  G: 10,   // Step
  H: 8,    // R/W
  I: 10,   // Scale
  J: 22,   // Reg Type
  K: 10,   // Register
  L: 8,    // Bit #
  M: 30,   // Data Type
};

const buildWorkbook = (device?: IDeviceInfoForExport, units?: IModbusDeviceUnit[]): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(SHEET_NAME);
  addDeviceInfoSection(sheet, device);
  const lastDataRow = addRegisterSection(sheet, units);
  addDropdownValidations(workbook, sheet, lastDataRow);
  Object.entries(COLUMN_WIDTHS).forEach(([col, width]) => {
    sheet.getColumn(col).width = width;
  });
  return workbook;
};

const downloadWorkbook = async (workbook: ExcelJS.Workbook, filename: string): Promise<void> => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, filename);
};

const sanitizeFileNamePart = (value: string): string =>
  value.trim().replace(/[\\/:*?"<>|]+/g, "_") || "device";

export const downloadModbusDeviceFile = async (
  device: IDeviceInfoForExport,
  units: IModbusDeviceUnit[],
): Promise<void> => {
  const workbook = buildWorkbook(device, units);
  await downloadWorkbook(workbook, `${sanitizeFileNamePart(device.name)}.xlsx`);
};

export const downloadModbusDeviceSampleTemplate = async (): Promise<void> => {
  const workbook = buildWorkbook();
  await downloadWorkbook(workbook, "modbus-device-template.xlsx");
};

/** Thrown when the uploaded file doesn't match the expected sheet layout at all — a hard stop. */
export class ModbusFileStructureError extends Error {}

/** A row-level outcome from parsing — mirrors the good/warning/error pattern used by the PPD tenant importer. */
export type ImportResultType = "good" | "warning" | "error";

export interface IImportResultMessage {
  rowNumber: number;
  type: ImportResultType;
  message: string;
}

export interface IParsedModbusDeviceFile {
  device: Partial<IDeviceInfoForExport>;
  units: IModbusDeviceUnit[];
  /** One entry per data row processed — imported cleanly ("good"), imported with a caveat ("warning"), or skipped ("error"). */
  messages: IImportResultMessage[];
}

const PLURAL_LABELS: Record<ImportResultType, [string, string]> = {
  good: ["row imported", "rows imported"],
  warning: ["warning", "warnings"],
  error: ["error", "errors"],
};

/** Mirrors the PPD tenant importer's `summarizeByCategory` — e.g. "12 rows imported, 2 warnings, 3 errors". */
export const summarizeImportMessages = (messages: IImportResultMessage[]): string => {
  const counts = messages.reduce((acc, m) => {
    acc[m.type] = (acc[m.type] ?? 0) + 1;
    return acc;
  }, {} as Record<ImportResultType, number>);

  return (["good", "warning", "error"] as ImportResultType[])
    .filter((type) => counts[type])
    .map((type) => {
      const [singular, plural] = PLURAL_LABELS[type];
      const count = counts[type];
      return `${count} ${count === 1 ? singular : plural}`;
    })
    .join(", ");
};

const cellText = (cell: ExcelJS.Cell): string => {
  const value = cell.value;
  if (value === null || value === undefined) {
    return "";
  }
  if (typeof value === "object" && "text" in (value as any)) {
    return String((value as any).text ?? "").trim();
  }
  if (typeof value === "object" && "result" in (value as any)) {
    return String((value as any).result ?? "").trim();
  }
  return String(value).trim();
};

const numberOrEmpty = (text: string): number | "" => (text === "" ? "" : Number(text));
const numberOrNull = (text: string): number | null => (text === "" ? null : Number(text));

const valueFor = (
  options: readonly { value: string | number; label: string }[],
  label: string,
): string | number | undefined => {
  const normalized = label.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  return options.find((o) => o.label.trim().toLowerCase() === normalized)?.value;
};

/**
 * @param isTcp Whether the device being imported into is on a TCP line — Host/Port are only
 * required in that case (same condition `DeviceMapper`'s own save validation uses), and the
 * file itself has no way to say which kind of line it's for.
 */
export const parseModbusDeviceFile = async (file: File, isTcp: boolean): Promise<IParsedModbusDeviceFile> => {
  const workbook = new ExcelJS.Workbook();
  try {
    const buffer = await file.arrayBuffer();
    await workbook.xlsx.load(buffer as any);
  } catch {
    throw new ModbusFileStructureError(t`Could not read the file. Make sure it's a valid .xlsx file.`);
  }

  const sheet = workbook.worksheets[0];
  if (!sheet || cellText(sheet.getCell("A1")).toLowerCase() !== "device information") {
    throw new ModbusFileStructureError(t`Unrecognized file structure.`);
  }
  const headerRow = sheet.getRow(HEADER_ROW);
  const headersMatch = REGISTER_COLUMNS.every(
    (col, i) => cellText(headerRow.getCell(i + 1)).toLowerCase() === col.toLowerCase(),
  );
  if (!headersMatch) {
    throw new ModbusFileStructureError(t`Unrecognized file structure.`);
  }

  const device: Partial<IDeviceInfoForExport> = {
    name: cellText(sheet.getCell("B2")),
    vendorName: cellText(sheet.getCell("B3")),
    modelName: cellText(sheet.getCell("B4")),
    slaveId: numberOrEmpty(cellText(sheet.getCell("B5"))),
    timeoutMs: numberOrEmpty(cellText(sheet.getCell("B6"))),
    host: cellText(sheet.getCell("B7")),
    port: numberOrEmpty(cellText(sheet.getCell("B8"))),
  };

  const messages: IImportResultMessage[] = [];
  const error = (rowNumber: number, message: string) => messages.push({ rowNumber, type: "error", message });
  const warn = (rowNumber: number, message: string) => messages.push({ rowNumber, type: "warning", message });
  const good = (rowNumber: number, message: string) => messages.push({ rowNumber, type: "good", message });

  // Row 1 is the "Device information" banner — reuse that row number so this
  // entry sorts first, ahead of the register rows below it in the sheet.
  const missingDeviceFields = [
    !device.name && t`Device Name`,
    !device.vendorName && t`Manufacturer`,
    !device.modelName && t`Model`,
    device.slaveId === "" && t`Slave ID`,
    device.timeoutMs === "" && t`Comm Timeout (ms)`,
    // Host/Port only matter for a TCP line — same condition DeviceMapper's own save validation uses.
    isTcp && !device.host && t`Host`,
    isTcp && device.port === "" && t`Port`,
  ].filter(Boolean) as string[];
  if (missingDeviceFields.length > 0) {
    warn(1, t`Device information — missing ${missingDeviceFields.join(", ")}; fill in before saving`);
  } else {
    const detailParts = [
      t`Device Name "${device.name}"`,
      t`Manufacturer "${device.vendorName}"`,
      t`Model "${device.modelName}"`,
      isTcp ? t`Host "${device.host}"` : t`Slave ID ${device.slaveId}`,
      isTcp ? t`Port ${device.port}` : "",
      t`Comm Timeout ${device.timeoutMs}ms`,
    ].filter(Boolean);
    good(1, t`Device information — Will import: ${detailParts.join(", ")}`);
  }

  const unitsByName = new Map<string, IModbusDeviceUnit>();
  const orderedUnits: IModbusDeviceUnit[] = [];

  let dataRowCount = 0;
  for (let rowNumber = FIRST_DATA_ROW; rowNumber <= sheet.rowCount; rowNumber++) {
    const row = sheet.getRow(rowNumber);
    const cells = REGISTER_COLUMNS.map((_, i) => cellText(row.getCell(i + 1)));
    if (cells.every((c) => !c)) {
      continue; // allow blank rows in between
    }

    dataRowCount += 1;
    if (dataRowCount > MAX_DATA_ROWS) {
      throw new ModbusFileStructureError(t`File exceeds the maximum allowed rows (${MAX_DATA_ROWS}).`);
    }

    const [unitName, paramName, typeLabel, engUnitText, minText, maxText, stepText, rwText, scaleText, regTypeLabel, registerText, bitText, dataTypeLabel] = cells;

    if (!unitName || !paramName) {
      error(rowNumber, t`Row #${rowNumber} — Cannot import: missing App Unit Name or Parameter Name`);
      continue;
    }
    const registerType = valueFor(REG_TYPE_NUMERIC_OPTIONS, regTypeLabel) as number | undefined;
    if (registerType === undefined) {
      error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": unrecognized Reg Type "${regTypeLabel}"`);
      continue;
    }
    let registerAddress: number | null = null;
    let registerWasBlank = false;
    if (registerText !== "") {
      const parsedAddress = Number(registerText);
      if (!Number.isFinite(parsedAddress)) {
        error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": invalid Register "${registerText}"`);
        continue;
      }
      registerAddress = parsedAddress;
    } else {
      registerWasBlank = true;
    }
    let dataType = valueFor(DATA_TYPE_CODE_OPTIONS, dataTypeLabel) as string | undefined;
    if (!dataType && dataTypeLabel.trim().toLowerCase() in MODBUS_DATA_TYPE_CODES) {
      dataType = dataTypeLabel.trim().toLowerCase();
    }
    if (!dataType) {
      error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": unrecognized Data Type "${dataTypeLabel}"`);
      continue;
    }
    const uiType = valueFor(UI_TYPE_OPTIONS, typeLabel) as number | undefined;
    if (uiType === undefined) {
      error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": unrecognized Type "${typeLabel}"`);
      continue;
    }
    const rw = rwText.trim().toUpperCase();
    let readOnly: boolean;
    if (rw === "R") {
      readOnly = true;
    } else if (rw === "R/W") {
      readOnly = false;
    } else {
      error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": unrecognized R/W "${rwText}"`);
      continue;
    }
    const engUnitValue = valueFor(ENG_UNIT_OPTIONS, engUnitText);
    let units: string | null;
    if (engUnitValue !== undefined) {
      units = String(engUnitValue);
    } else if (!engUnitText) {
      units = null;
    } else {
      error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": unrecognized Eng Units "${engUnitText}"`);
      continue;
    }
    // Valid range mirrors BIT_NUMBER_OPTIONS (0-15) in AddFunctionalUnitModal.
    const bitNumber = numberOrNull(bitText);
    if (bitNumber !== null && (!Number.isInteger(bitNumber) || bitNumber < 0 || bitNumber > 15)) {
      error(rowNumber, t`Row #${rowNumber} — Cannot import "${paramName}": invalid Bit # "${bitText}"`);
      continue;
    }

    const deviceRow: IModbusDeviceRow = {
      name: paramName,
      readOnly,
      uiType,
      units,
      minValue: numberOrNull(minText),
      maxValue: numberOrNull(maxText),
      step: numberOrNull(stepText),
      registerType: registerType as number,
      registerAddress,
      dataType,
      bitNumber,
      multiplier: scaleText === "" ? 1 : Number(scaleText),
    };

    let unit = unitsByName.get(unitName);
    if (!unit) {
      unit = { name: unitName, rows: [] };
      unitsByName.set(unitName, unit);
      orderedUnits.push(unit);
    }
    unit.rows.push(deviceRow);

    if (registerWasBlank) {
      warn(rowNumber, t`Row #${rowNumber} — Will import "${paramName}" into "${unitName}" without a Register; map it later`);
    } else {
      good(rowNumber, t`Row #${rowNumber} — Will import "${paramName}" into "${unitName}"`);
    }
  }

  return { device, units: orderedUnits, messages };
};
